@use '@/styles/tokens' as *;

.body {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Header cluster, rendered inside the drawer's cream title band (Figma 325:264859) */
.header {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
}

.iconBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px; // Figma 325:264861 — fixed 32×32 badge, no size token
  height: 32px;
  border-radius: $radius-pill;
  // Figma 325:264862 — Accent 1/accent-900, not the queue twin's $color-brand-700.
  background: $color-accent1-900;
  color: var(--text-inverse);
}

.headerHeading {
  font-size: 18px; // Main Font-Geist/18/M — no $font-size-* token at 18
  font-weight: $font-weight-medium;
  color: $color-neutral-900;
}

/* Item name + category (Figma 325:264807) */
.section {
  display: flex;
  flex-direction: column;
  gap: 2px; // Figma — below the 4px token floor
  margin-bottom: var(--space-3);
}

.name {
  font-size: $font-size-lg;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
  overflow-wrap: break-word; // wraps, never truncates (unlike the twin's device name)
}

.category {
  font-size: $font-size-xs;
  color: var(--text-muted);
}

/* Evidence thumbnails (Figma 325:264811).
   A fixed 4-column grid, NOT flex-wrap: the AC requires "the first four in the top row followed by
   the 5th", and flex-wrap only achieved that by arithmetic luck. The drawer body is 416px wide
   (464 - 2×24 chrome padding), and 4×92 + 3×16 = 416 exactly — zero slack, so the `overflow-y:auto`
   body plus a classic space-consuming scrollbar wrapped the FOURTH tile and rendered 3+2. Four
   explicit columns guarantee the layout at any body width; at 416 each cell computes to Figma's
   92px ((416 - 3×16) / 4), and under a scrollbar the tiles shrink instead of wrapping. */
.thumbGrid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4); // Figma 17.25px — no token; 16px is nearest
  margin: 0 0 var(--space-5);
  padding: 0;
  list-style: none;
}

.thumbBtn {
  width: 100%; // grid cell owns the width — 92px at the drawer's 464px (Figma 325:264812)
  aspect-ratio: 1; // keeps the Figma square as the cell flexes
  padding: 0;
  border: none; // Figma draws no border and no radius on these tiles
  background: $color-neutral-100; // Figma #F6F8F7 — nearest token
  overflow: hidden;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

.thumbImg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Reason + description (Figma 325:264824): white card, subtle border, 10px radius */
.reasonCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0 0 var(--space-5);
  padding: 19px 17px; // Figma 325:264824 — neither pad has a --space token
  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 10px; // Figma — between $radius-lg (8) and $radius-xl (12)
}

.reasonHeading {
  margin: 0;
  font-size: 15px; // Main Font-Geist/15/M — no $font-size-* token at 15
  font-weight: $font-weight-medium;
  color: var(--text-tertiary);
}

.description {
  margin: 0;
  font-size: $font-size-md;
  line-height: 1.5;
  color: var(--text-tertiary);
  overflow-wrap: break-word; // AC: wraps rather than truncating
}

/* Activity Timeline heading (Figma 429:311619: Geist 14/R, muted slate #6A7282) */
.timelineHeading {
  margin: 0 0 var(--space-6);
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: $color-neutral-600;
}

/* Approved: replacement-order band (Figma 325:274328) */
.orderBand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin: 0 0 var(--space-5);
  padding: var(--space-2) var(--space-3);
  background: var(--surface-subtle);
  border-radius: $radius-xl;
}

.orderBandText {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.orderBandCaption {
  font-size: $font-size-xs;
  color: var(--text-muted);
}

.orderBandNumber {
  font-size: $font-size-md;
  font-weight: $font-weight-medium;
  color: var(--text-primary);
}

/* Rejected: reason card, below the timeline (Figma 325:269607) — inverted from the queue twin,
   where it sits above. This inversion is why this drawer is a sibling, not a shared component. */
.rejection {
  display: flex;
  flex-direction: column;
  gap: 6px; // Figma — below the 8px token step
  margin: 0;
  padding: var(--space-3);
  background: $color-error-50;
  border-radius: $radius-xl;
}

.rejectionHeading {
  display: inline-flex;
  align-items: center;
  gap: 6px; // Figma 325:269608
  font-size: $font-size-md;
  font-weight: $font-weight-regular;
  color: $color-stage-attention-text;
}

.rejectionValue {
  margin: 0;
  font-size: $font-size-xs;
  color: $color-error-950;
  overflow-wrap: break-word;
}

// Failure copy is centred in the body, matching the listing drawer. Never a fixed padding-block —
// Figma centres states it didn't draw, and a top-offset state was a real review defect on ticket 03.
.notice {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin: 0;
  text-align: center;
  color: var(--text-primary);
}
